import React, { useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { firstApiErrorMessage } from '../../../models/ApiErrors'
import { Modal } from '../../../components/Modal/Modal'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useTestDataSourceMutation } from '../../../store/integrations/externalDataSources'
import { type ExternalDataSource, type FetchTestOutcome, type TestFetchResult } from '../../../models/ExternalDataSource'

interface TestDataSourceModalProps {
  show: boolean
  clientId: string
  dataSource: ExternalDataSource
  onHide: () => void
  onExited?: () => void
}

const OUTCOME_LABELS: Record<FetchTestOutcome, string> = {
  success: 'The endpoint answered',
  failed: 'The endpoint failed',
  timeout: 'The endpoint timed out',
}

export const TestDataSourceModal: React.FC<TestDataSourceModalProps> = ({ show, clientId, dataSource, onHide, onExited }) => {
  const accountId = useAppSelector(selectAccountId)
  const [testDataSource, { isLoading }] = useTestDataSourceMutation()

  const [contactId, setContactId] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [result, setResult] = useState<TestFetchResult | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [validated, setValidated] = useState<boolean>(false)

  const handleRun = useCallback(async () => {
    setValidated(true)
    if (contactId.trim() === '') {
      return
    }
    setRequestError(null)
    try {
      const response = await testDataSource({
        accountId,
        clientId,
        dataSourceId: dataSource.id,
        subject: { type: 'contact', id: contactId.trim(), ...(email.trim() === '' ? {} : { email: email.trim() }) },
      }).unwrap()
      setResult(response.data)
    } catch (error) {
      setResult(null)
      setRequestError(firstApiErrorMessage(error) ?? 'The test could not be run. Please try again.')
    }
  }, [contactId, email, testDataSource, accountId, clientId, dataSource.id])

  const succeeded = result?.outcome === 'success'

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={onExited}
      header={`Test ‘${dataSource.name}’`}
      body={
        <div>
          <p className='paragraph-small zocks-text-gray-600'>
            Name a contact you can open. Zocks sends a real signed fetch for it and shows you exactly what came back. A passing test is what activates the data
            source.
          </p>

          <Form.Group className='mb-3'>
            <Form.Label>Contact ID</Form.Label>
            <Form.Control
              type='text'
              placeholder='e.g. 7h3k9m2p4q6r8s1t'
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              isInvalid={validated && contactId.trim() === ''}
            />
            <Form.Control.Feedback type='invalid'>A contact ID is required.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>
              Email <span className='zocks-text-gray-600'>(optional)</span>
            </Form.Label>
            <Form.Control type='email' placeholder='jane@example.com' value={email} onChange={(e) => setEmail(e.target.value)} />
            <Form.Text className='zocks-text-gray-600'>Sent as part of the subject, if your endpoint keys off it.</Form.Text>
          </Form.Group>

          {requestError ? (
            <div className='p-3 rounded-2 zocks-bg-color-danger-background zocks-border-color-danger'>
              <p className='paragraph-small mb-0'>{requestError}</p>
            </div>
          ) : null}

          {result ? (
            <div
              className={classNames(
                'p-3 rounded-2',
                succeeded ? 'zocks-bg-color-success-background zocks-border-color-success' : 'zocks-bg-color-danger-background zocks-border-color-danger',
              )}
            >
              <div className='d-flex align-items-center zocks-gap-0.5 mb-2'>
                <i className={classNames('bi', succeeded ? 'bi-check-circle-fill' : 'bi-x-circle-fill')} />
                <span className='fw-bold'>{OUTCOME_LABELS[result.outcome]}</span>
                <span className='paragraph-small zocks-text-gray-700'>
                  {result.statusCode === null ? null : `HTTP ${result.statusCode}`}
                  {result.durationMs === null ? null : ` · ${result.durationMs} ms`}
                </span>
              </div>
              {result.error ? <p className='paragraph-small mb-2'>{result.error}</p> : null}
              <p className='paragraph-small mb-2'>
                Data source is now <strong>{result.status}</strong>.
              </p>
              {result.sampleResponse === null || result.sampleResponse === undefined ? null : (
                <pre className='paragraph-small mb-0 overflow-auto'>{JSON.stringify(result.sampleResponse, null, 2)}</pre>
              )}
            </div>
          ) : null}
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle={result ? 'Run again' : 'Run test'}
      footerSecondaryActionTitle='Close'
      onFooterPrimaryAction={handleRun}
      onFooterSecondaryAction={onHide}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={isLoading}
    />
  )
}
